// Enemy data types and loader

export type ElementType = 'THERMAL' | 'CRYO' | 'RADIATION' | 'EMP' | 'VOID';

export interface EnemyResists {
    THERMAL: number;
    CRYO: number;
    RADIATION: number;
    EMP: number;
    VOID: number;
}

export interface EnemyTemplate {
    id: string;
    name: string;
    baseHP: number;
    hpPerRow: number;
    resists: EnemyResists;
    defaultCooldown: number;
    artKey: string;
    aspects: ElementType[];
}

export interface EnemyEffectPoolEntry {
    effectId: string;
    difficultyCost: number;
    cooldown: number;
    params: Record<string, unknown>;
    tags: string[];
    rowMin: number;
    threatMin: number;
    tier: number;
}

export interface BossAction {
    effectId: string;
    cooldown: number;
    params: Record<string, unknown>;
}

export interface BossTemplate {
    id: string;
    name: string;
    blueprintDrop: string;
    baseHP: number;
    resists: EnemyResists;
    actions: BossAction[];
    aspects: ElementType[];
}

export interface EnemyGenerationConfig {
    fightBudgetByRow: number[];
    treasureBudgetByRow: number[];
    eliteBudgetByRow: number[];
    bossBudgetByRow: number[];
}

export interface EnemyData {
    version: string;
    generation: EnemyGenerationConfig;
    enemyTemplates: EnemyTemplate[];
    enemyEffectPool: EnemyEffectPoolEntry[];
    bosses: BossTemplate[];
}

// Enemy effect definition from enemy_effects.json
export interface EnemyEffectDefinition {
    id: string;
    type: string;
    paramsSchema: Record<string, string>;
}

export interface EnemyEffectsData {
    version: string;
    enemyEffects: EnemyEffectDefinition[];
}

// Cache for loaded data
let cachedEnemyData: EnemyData | null = null;
let cachedEnemyEffectsData: EnemyEffectsData | null = null;

export async function loadEnemyData(): Promise<EnemyData> {
    if (cachedEnemyData) {
        return cachedEnemyData;
    }

    const response = await fetch('/data/enemies.json');
    if (!response.ok) {
        throw new Error(`Failed to load enemies.json: ${response.statusText}`);
    }

    cachedEnemyData = await response.json();
    return cachedEnemyData!;
}

export function getEnemyData(): EnemyData {
    if (!cachedEnemyData) {
        throw new Error('Enemy data not loaded. Call loadEnemyData() first.');
    }
    return cachedEnemyData;
}

export async function loadEnemyEffectsData(): Promise<EnemyEffectsData> {
    if (cachedEnemyEffectsData) {
        return cachedEnemyEffectsData;
    }

    const response = await fetch('/data/enemy_effects.json');
    if (!response.ok) {
        throw new Error(`Failed to load enemy_effects.json: ${response.statusText}`);
    }

    cachedEnemyEffectsData = await response.json();
    return cachedEnemyEffectsData!;
}

export function getEnemyEffectsData(): EnemyEffectsData {
    if (!cachedEnemyEffectsData) {
        throw new Error('Enemy effects data not loaded. Call loadEnemyEffectsData() first.');
    }
    return cachedEnemyEffectsData;
}

export function getEnemyEffectType(effectId: string): string {
    if (!cachedEnemyEffectsData) {
        console.warn('Enemy effects data not loaded, inferring type from effectId');
        // Fallback: infer type from effectId prefix
        if (effectId.includes('attack')) return 'ENEMY_ATTACK';
        if (effectId.includes('lock_cells')) return 'BOARD_LOCK_CELLS';
        if (effectId.includes('spawn_bias')) return 'BOARD_SPAWN_BIAS';
        if (effectId.includes('convert_random')) return 'BOARD_CONVERT_RANDOM';
        if (effectId.includes('reduce_move_time')) return 'DEBUFF_MOVE_TIME';
        if (effectId.includes('shield_drain')) return 'SHIELD_DRAIN';
        if (effectId.includes('heal_block')) return 'DEBUFF_HEALING';
        if (effectId.includes('gain_resist')) return 'ENEMY_GAIN_RESIST';
        if (effectId.includes('enrage') || effectId.includes('damage_up')) return 'ENEMY_DAMAGE_MULT';
        return 'ENEMY_ATTACK'; // Default to attack
    }
    const effect = cachedEnemyEffectsData.enemyEffects.find(e => e.id === effectId);
    return effect?.type || 'ENEMY_ATTACK';
}

// Helper functions

export function getTemplatesMatchingAspects(aspects: ElementType[]): EnemyTemplate[] {
    const data = getEnemyData();
    return data.enemyTemplates.filter(template =>
        template.aspects.some(aspect => aspects.includes(aspect))
    );
}

export function getBossById(bossId: string): BossTemplate | undefined {
    const data = getEnemyData();
    return data.bosses.find(boss => boss.id === bossId);
}

export function getRandomBoss(): BossTemplate {
    const data = getEnemyData();
    const index = Math.floor(Math.random() * data.bosses.length);
    return data.bosses[index];
}

export function getBudgetsForThreatLevel(threatLevel: number): {
    fight: number;
    treasure: number;
    elite: number;
    boss: number;
} {
    const data = getEnemyData();
    const index = Math.max(0, Math.min(9, threatLevel - 1)); // Clamp to 0-9
    return {
        fight: data.generation.fightBudgetByRow[index],
        treasure: data.generation.treasureBudgetByRow[index],
        elite: data.generation.eliteBudgetByRow[index],
        boss: data.generation.bossBudgetByRow[index],
    };
}

export function getEffectsForThreatLevel(threatLevel: number, tags?: string[]): EnemyEffectPoolEntry[] {
    const data = getEnemyData();
    return data.enemyEffectPool.filter(effect => {
        if (effect.threatMin > threatLevel) return false;
        if (tags && !tags.some(tag => effect.tags.includes(tag))) return false;
        return true;
    });
}

// Threat level scaling multipliers (from design doc)
export function getThreatMultipliers(threatLevel: number): {
    hpMult: number;
    dmgMult: number;
    effectMult: number;
    rewardMult: number;
} {
    const t = threatLevel - 1;
    return {
        hpMult: 1 + 0.09 * t,
        dmgMult: 1 + 0.10 * t,
        effectMult: 1 + 0.08 * t,
        rewardMult: 1 + 0.50 * t,  // 0.5x extra per level above 1 (L2=1.5x, L10=5.5x)
    };
}

// Aspect color mapping for tinting
export const ASPECT_COLORS: Record<ElementType, number> = {
    THERMAL: 0xe74c3c,   // Red
    CRYO: 0x3498db,      // Blue
    RADIATION: 0x2ecc71, // Green
    EMP: 0xf1c40f,       // Yellow
    VOID: 0x9b59b6,      // Purple
};

export const ASPECT_DARK_COLORS: Record<ElementType, number> = {
    THERMAL: 0x4a2020,
    CRYO: 0x203a4a,
    RADIATION: 0x204a20,
    EMP: 0x4a4a20,
    VOID: 0x3a204a,
};
